/**
 * <pcard-quick-add>
 *
 * A Princess-Polly-style "Quick Add" tab for a product card: a persistent
 * black bar under the image that, when clicked, opens a translucent white
 * "Select a Size" panel floated over the bottom of the product photo itself
 * (see 'pcard-quick-add-panel') -- no popup, no leaving the page, and the
 * bar never hides itself, matching Princess Polly's real behaviour exactly.
 *
 * The panel lives inside .product-card__image-wrapper (so its
 * position:absolute anchors to the image), which is outside this element's
 * own light DOM -- so it is looked up inside this card first, falling back
 * to its id (data-panel-id, written by 'pcard-quick-add-tab'). Looking inside
 * the card first matters on the home page's tabbed product section: the same
 * product can appear in two tabs, which gives both cards the same panel id,
 * and a plain getElementById would open the hidden tab's panel instead.
 *
 * The actual add-to-cart request is handled entirely by the theme's existing
 * is="product-form" custom element (see theme.js -- ProductForm), which
 * already does the AJAX post, cart update, and opening the cart drawer. This
 * component only has to open/close the panel, keep its form's hidden
 * variant-id input pointed at whichever size was clicked, and trigger the
 * form submit -- everything else is inherited for free.
 */
(function () {
  if (customElements.get('pcard-quick-add')) return;

  class PcardQuickAdd extends HTMLElement {
    connectedCallback() {
      this.tab = this.querySelector('.product-card__quick-add-tab');
      const card = this.closest('.product-card');
      this.panel = (card && card.querySelector('.product-card__quick-add-panel')) || document.getElementById(this.getAttribute('data-panel-id'));
      this.form = this.panel ? this.panel.querySelector('form') : null;
      this.hiddenIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.pending = false;

      this.onTabClick = this.onTabClick.bind(this);
      this.onSizeClick = this.onSizeClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onAdded = this.onAdded.bind(this);
      this.onError = this.onError.bind(this);

      this.tab.addEventListener('click', this.onTabClick);
      if (this.panel) this.panel.addEventListener('click', this.onSizeClick);
      document.addEventListener('click', this.onDocumentClick);
      // Both events are dispatched globally (on `document`) by ProductForm, so
      // every card's instance hears every add-to-cart on the page -- `pending`
      // scopes the reaction to whichever card actually triggered the request.
      document.addEventListener('product-ajax:added', this.onAdded);
      document.addEventListener('product-ajax:error', this.onError);
    }

    disconnectedCallback() {
      if (this.panel) this.panel.removeEventListener('click', this.onSizeClick);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('product-ajax:added', this.onAdded);
      document.removeEventListener('product-ajax:error', this.onError);
    }

    onTabClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.panel) return;
      this.setExpanded(this.tab.getAttribute('aria-expanded') !== 'true');
    }

    setExpanded(expanded) {
      if (!this.panel) return;
      this.tab.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.panel.hidden = !expanded;
    }

    onDocumentClick(event) {
      const insideTab = this.contains(event.target);
      const insidePanel = this.panel && this.panel.contains(event.target);
      if (!insideTab && !insidePanel) this.setExpanded(false);
    }

    onSizeClick(event) {
      const button = event.target.closest('.product-card__quick-add-size');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      if (!this.form || !this.hiddenIdInput) return;

      event.preventDefault();
      event.stopPropagation();

      this.hiddenIdInput.value = button.getAttribute('data-variant-id');

      this.panel.querySelectorAll('.product-card__quick-add-size').forEach((b) => {
        b.classList.remove('is-selected', 'is-loading');
        // Disable the other sizes while this request is in flight, so a second
        // click can't fire a second add before the first one resolves.
        if (b !== button) b.setAttribute('aria-disabled', 'true');
      });
      button.classList.add('is-selected', 'is-loading');
      button.removeAttribute('aria-disabled');

      this.pending = true;
      this.form.requestSubmit();
    }

    clearLoadingState() {
      if (!this.panel) return;
      this.panel.querySelectorAll('.product-card__quick-add-size').forEach((b) => {
        b.classList.remove('is-loading');
        b.removeAttribute('aria-disabled');
      });
    }

    onAdded() {
      if (!this.pending) return;
      this.pending = false;
      this.clearLoadingState();
      // The cart drawer just opened (ProductForm's own doing) to confirm the
      // add -- that's the "added" feedback, so close the size panel.
      this.setExpanded(false);
    }

    onError() {
      if (!this.pending) return;
      this.pending = false;
      this.clearLoadingState();
      // Leave the panel open: ProductForm has already written the failure
      // reason into this form's own .product-form__error-message for us.
    }
  }

  customElements.define('pcard-quick-add', PcardQuickAdd);
})();
